/** @jsxImportSource @emotion/react */
import { useCallback, useMemo, useState } from 'react'
import { Button, Card, Form, Spinner } from 'react-bootstrap'
import Select from 'react-select'
import { skipToken } from '@reduxjs/toolkit/query'
import moment from 'moment'
import DataTable from 'components/atoms/DataTable'
import { TableWrapper } from 'components/atoms/TableWrapper'
import { TooltipWrapper } from 'components/atoms/TooltipWrapper'

import {
  useLazyGetAuditTrailQuery,
  AuditTrailItem,
  AuditTrailParams,
} from 'store/services/managementReports'
import { useGetLabUsersQuery } from 'store/services/users'
import { useLazyGetLabPatientListQuery } from 'store/services/patient'

type UserOption = { label: string; value: number }
type PatientOption = { label: string; value: number }

const PAGE_SIZE_DEFAULT = 20

export const AuditTrail = () => {
  const [selectedUser, setSelectedUser] = useState<UserOption | null>(null)
  const [selectedPatient, setSelectedPatient] = useState<PatientOption | null>(null)
  const [orderId, setOrderId] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [currentPage, setCurrentPage] = useState(0)
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT)
  const [hasSearched, setHasSearched] = useState(false)

  const [userSearch, setUserSearch] = useState('')
  const [patientSearch, setPatientSearch] = useState('')

  const { data: labUsersData } = useGetLabUsersQuery(
    userSearch.length >= 2
      ? { search: userSearch, page: 0, size: 50 }
      : skipToken,
  )

  const [fetchPatients, { data: patientData }] = useLazyGetLabPatientListQuery()

  const [triggerAuditTrail, { data: auditData, isFetching, isError }] =
    useLazyGetAuditTrailQuery()

  const userOptions: UserOption[] = useMemo(() => {
    if (!labUsersData) return []
    const items = Array.isArray(labUsersData) ? labUsersData : (labUsersData as any)?.items || []
    return items.map((u: any) => ({
      label: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email,
      value: u.id,
    }))
  }, [labUsersData])

  const patientOptions: PatientOption[] = useMemo(() => {
    if (!patientData) return []
    const items = Array.isArray(patientData) ? patientData : (patientData as any)?.items || []
    return items.map((p: any) => ({
      label: `${p.firstName || ''} ${p.lastName || ''}`.trim() + (p.dob ? ` (DOB: ${p.dob})` : ''),
      value: p.id,
    }))
  }, [patientData])

  const handlePatientSearch = useCallback(
    (inputValue: string) => {
      setPatientSearch(inputValue)
      if (inputValue.length >= 2) {
        fetchPatients({ search: inputValue, page: 0, size: 50 })
      }
    },
    [fetchPatients],
  )

  const hasOrderId = orderId.trim().length > 0
  const hasDateRange = dateFrom.length > 0 && dateTo.length > 0
  const hasUserOrPatient = !!selectedUser || !!selectedPatient

  const isValid = hasOrderId || (hasDateRange && hasUserOrPatient)

  const validationHint = useMemo(() => {
    if (isValid) return null
    if (hasDateRange && !hasUserOrPatient) {
      return 'Select a user or patient to search by date range.'
    }
    if (hasUserOrPatient && !hasDateRange) {
      return 'Provide a date range when searching by user or patient.'
    }
    return 'Enter an order ID, or select a user/patient with a date range.'
  }, [isValid, hasDateRange, hasUserOrPatient])

  const buildParams = useCallback(
    (page: number): AuditTrailParams => {
      const params: AuditTrailParams = { page, size: pageSize }
      if (orderId.trim()) params.orderId = Number(orderId.trim())
      if (selectedUser) params.userId = selectedUser.value
      if (selectedPatient) params.patientId = selectedPatient.value
      if (dateFrom) params.from = dateFrom
      if (dateTo) params.to = dateTo
      return params
    },
    [orderId, selectedUser, selectedPatient, dateFrom, dateTo, pageSize],
  )

  const handleSearch = useCallback(() => {
    if (!isValid) return
    setCurrentPage(0)
    setHasSearched(true)
    triggerAuditTrail(buildParams(0))
  }, [isValid, buildParams, triggerAuditTrail])

  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(page)
      triggerAuditTrail(buildParams(page))
    },
    [buildParams, triggerAuditTrail],
  )

  const handleClear = useCallback(() => {
    setSelectedUser(null)
    setSelectedPatient(null)
    setOrderId('')
    setDateFrom('')
    setDateTo('')
    setCurrentPage(0)
    setHasSearched(false)
  }, [])

  const handleExportCsv = useCallback(() => {
    if (!auditData?.items?.length) return
    const headers = ['Timestamp', 'User', 'Email', 'Order ID', 'Action', 'Source', 'IP Address', 'User Agent']
    const rows = auditData.items.map((item: AuditTrailItem) => [
      moment(item.timestamp).format('YYYY-MM-DD HH:mm:ss'),
      `"${item.userName}"`,
      item.userEmail,
      item.orderId,
      `"${item.action.replace(/"/g, '""')}"`,
      item.activitySource,
      item.ipAddress || '',
      `"${(item.userAgent || '').replace(/"/g, '""')}"`,
    ])
    const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `audit-trail-${moment().format('YYYY-MM-DD')}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }, [auditData])

  const tableColumns = useMemo(
    () => [
      {
        Header: 'Timestamp',
        accessor: 'timestamp',
        Cell: ({ value }: { value: string }) => moment(value).format('MM/DD/YYYY hh:mm:ss A'),
        className: 'text-nowrap',
      },
      { Header: 'User', accessor: 'userName' },
      { Header: 'Email', accessor: 'userEmail' },
      { Header: 'Order ID', accessor: 'orderId' },
      {
        Header: 'Action',
        accessor: 'action',
        Cell: ({ value, row }: { value: string; row: { original: AuditTrailItem } }) => (
          <TooltipWrapper
            text={row.original.documentName || ''}
            show={value === 'Downloaded document' && !!row.original.documentName}>
            <span>{value}</span>
          </TooltipWrapper>
        ),
      },
      { Header: 'Source', accessor: 'activitySource' },
      {
        Header: 'IP Address',
        accessor: 'ipAddress',
        Cell: ({ value }: { value: string | null }) => value || '—',
      },
      {
        Header: 'User Agent',
        accessor: 'userAgent',
        Cell: ({ value }: { value: string | null }) => (
          <span title={value || ''} css={{ maxWidth: 200, display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {value || '—'}
          </span>
        ),
      },
    ],
    [],
  )

  const tableData = useMemo(() => {
    if (!auditData) return { items: [], currentPage: 0, totalItems: 0 }
    return {
      items: auditData.items,
      currentPage: auditData.currentPage,
      totalItems: auditData.totalItems,
    }
  }, [auditData])

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <Card className="mt-3">
            <Card.Header>
              <div className="d-flex flex-wrap align-items-center justify-content-between">
                <h4 className="mb-0">HIPAA Audit Trail — Order Access Log</h4>
                {isFetching && <Spinner animation="border" size="sm" />}
              </div>
            </Card.Header>
            <Card.Body>
              <div className="row mb-3">
                <div className="col-md-3 col-sm-12 mb-2">
                  <label className="mb-1">Order ID</label>
                  <Form.Control
                    type="text"
                    placeholder="e.g. 12345"
                    value={orderId}
                    onChange={(e) => setOrderId(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  />
                </div>
                <div className="col-md-3 col-sm-12 mb-2">
                  <label className="mb-1">User</label>
                  <Select
                    classNamePrefix="react-select"
                    isClearable
                    placeholder="Search by name..."
                    value={selectedUser}
                    onInputChange={(val) => setUserSearch(val)}
                    onChange={(option) => setSelectedUser(option as UserOption | null)}
                    options={userOptions}
                    noOptionsMessage={() =>
                      userSearch.length < 2 ? 'Type at least 2 characters...' : 'No users found'
                    }
                  />
                </div>
                <div className="col-md-3 col-sm-12 mb-2">
                  <label className="mb-1">Patient</label>
                  <Select
                    classNamePrefix="react-select"
                    isClearable
                    placeholder="Search by name..."
                    value={selectedPatient}
                    onInputChange={handlePatientSearch}
                    onChange={(option) => setSelectedPatient(option as PatientOption | null)}
                    options={patientOptions}
                    noOptionsMessage={() =>
                      patientSearch.length < 2 ? 'Type at least 2 characters...' : 'No patients found'
                    }
                  />
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-md-3 col-sm-12 mb-2">
                  <label className="mb-1">Date From</label>
                  <Form.Control
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                  />
                </div>
                <div className="col-md-3 col-sm-12 mb-2">
                  <label className="mb-1">Date To</label>
                  <Form.Control
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                  />
                </div>
                <div className="col-md-6 col-sm-12 mb-2 d-flex align-items-end">
                  <Button variant="primary" onClick={handleSearch} disabled={!isValid || isFetching}>
                    {isFetching ? <Spinner animation="border" size="sm" className="mr-1" /> : null}
                    Search
                  </Button>
                  <Button variant="outline-secondary" className="ml-2" onClick={handleClear}>
                    Clear
                  </Button>
                  {hasSearched && auditData?.items?.length ? (
                    <Button variant="outline-success" className="ml-2" onClick={handleExportCsv}>
                      Export CSV
                    </Button>
                  ) : null}
                </div>
              </div>
              {validationHint && (
                <div className="text-muted mb-3">
                  <small><i className="fas fa-info-circle mr-1" />{validationHint}</small>
                </div>
              )}
            </Card.Body>
          </Card>
        </div>
      </div>

      {hasSearched && (
        <div className="row">
          <div className="col-12">
            <Card className="mt-3">
              <Card.Header>
                <div className="d-flex flex-wrap align-items-center justify-content-between">
                  <h4 className="mb-0">
                    Results
                    {auditData ? ` (${auditData.totalItems.toLocaleString()} records)` : ''}
                  </h4>
                  {isFetching && <Spinner animation="border" size="sm" />}
                </div>
              </Card.Header>
              <Card.Body>
                {isError ? (
                  <div className="alert alert-danger">
                    Failed to load audit trail. Please check your filters and try again.
                  </div>
                ) : (
                  <TableWrapper className="card-body row">
                    <DataTable
                      tableKey="audit_trail"
                      data={tableData}
                      status={isFetching}
                      tableColumns={tableColumns}
                      pageSize={pageSize}
                      setCurrentPage={handlePageChange}
                      setPageSize={setPageSize}
                      setSortOptions={() => {}}
                      searchable={false}
                      entityName="audit records"
                      tableDataEmptyText="No audit records found for the selected filters."
                    />
                  </TableWrapper>
                )}
              </Card.Body>
            </Card>
          </div>
        </div>
      )}

      {!hasSearched && (
        <div className="row">
          <div className="col-12">
            <Card className="mt-3">
              <Card.Body className="text-center text-muted py-5">
                <i className="fas fa-search fa-3x mb-3" css={{ opacity: 0.3 }} />
                <p className="mb-0">Use the filters above to search the audit trail.</p>
                <p>
                  <small>You can search by order ID alone, or by user/patient with a date range.</small>
                </p>
              </Card.Body>
            </Card>
          </div>
        </div>
      )}
    </div>
  )
}

export default AuditTrail
